import type { PartnersLocationsByIdsQuery } from 'graphql/research/types-and-hooks';
import type { QueryError, SingleNodeQueryProps } from 'lib/api';

import haversineDistance from 'haversine-distance';
import { queryResearch } from 'lib/apollo';
import { QueryOptions } from '@apollo/client';

export type QueryLocation = {
  id: number;
  name?: string;
  google_location: Partial<
    PartnersLocationsByIdsQuery['nodes'][0]['google_location']
  >;
};

export type LocationCoordinates = { lat: number; lng: number };

export type LocationQueryHours = {
  close?: {
    day: string;
    time: string;
  };
  open?: {
    day: string;
    time: string;
  };
};

export type LocationHoursDay = {
  close?: number;
  open?: number;
};

export type LocationHoursWeek = LocationHoursDay[];

export type LocationItem = LocationCoordinates & {
  id: number | string;
  category?: string;
  features?: string[];
  formattedAddress: string;
  name: string;
  rating: number;
  state: string;
  openingHours?: LocationHoursWeek;
  placeId?: string;
  internationalPhoneNumber?: string;
  uri?: string;
  userRatingsTotal: number;
};

export type LocationItemDistance = LocationItem & { distance: number };

export type LocationItems = LocationItem[];

export type GetLocationsNearby = {
  data: QueryLocation[];
};

export type GetLocationsNearbyVariables = Pick<
  SingleNodeQueryProps,
  'client'
> & {
  variables: { lat: number; lng: number; excludeId: number };
} & {
  query: QueryOptions['query'];
};

export async function getLocationsNearby<
  T extends {
    nodes: { google_location: { lat: number | null; lng: number | null } }[];
  },
>({
  client,
  query,
  variables: { lat, lng, excludeId },
}: GetLocationsNearbyVariables): Promise<QueryError & GetLocationsNearby> {
  const roundedLat = Math.floor(lat);
  const roundedLng = Math.floor(lng);

  const { data, error } = await queryResearch<T>(client, {
    query,
    variables: {
      excludeId,
      // 1º latitude is roughly equal to 69miles (111km)
      // 1/60 of a degree is approximately one mile
      fromLat: roundedLat - 2,
      toLat: roundedLat + 2,
      // At 40 degrees north or south, the distance between a degree of longitude is 53 miles (85 km)
      // The line at 40 degrees north runs through the middle of the US
      fromLng: roundedLng - 2,
      toLng: roundedLng + 2,
    },
  });

  if (error) {
    return { error, data: null };
  }

  const locations = data?.nodes;

  if (!locations?.length) {
    return { data: null, error };
  }

  const sortedItems = sortLocationsByDistance<QueryLocation>({
    locations: locations.map(({ google_location, ...rest }) => ({
      google_location,
      lat: google_location.lat,
      lng: google_location.lng,
      ...rest,
    })),
    center: { lat, lng },
  });

  return { data: sortedItems.slice(0, 3), error };
}

export function getLocationDayTimestamp({
  weekday,
  time,
}: {
  time?: string;
  weekday: number;
}): number | null {
  if (!time || !weekday) {
    return null;
  }

  const date = new Date();

  // set correct weekday
  // query returns weekdays 1-7 and Date expects 0-6
  const currentDay = date.getDay();
  const distance = weekday - 1 - currentDay;

  date.setDate(date.getDate() + distance);

  const timeArray = time.split(':');

  return Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    parseInt(timeArray[0]),
    parseInt(timeArray[1]),
  );
}

export function getLocationSchedules(
  openingHours: LocationQueryHours[],
): LocationHoursWeek {
  if (!openingHours?.length) {
    return null;
  }

  const dateTimeSchedules: Record<number, LocationHoursDay> =
    openingHours.reduce((schedules, item) => {
      const dayClose = item?.close?.day ? parseInt(item.close.day) : null;
      const dayOpen = item?.open?.day ? parseInt(item.open.day) : null;

      if (dayOpen) {
        schedules[dayOpen] = schedules?.[dayOpen] || {};

        const open = getLocationDayTimestamp({
          weekday: dayOpen,
          time: item?.open?.time,
        });

        schedules[dayOpen]['open'] = open;
      }

      if (dayClose) {
        schedules[dayClose] = schedules?.[dayClose] || {};

        const close = getLocationDayTimestamp({
          weekday: dayClose,
          time: item?.close?.time,
        });

        schedules[dayClose]['close'] = close;
      }

      return schedules;
    }, {});

  const arraySchedules = Object.values(dateTimeSchedules);

  return arraySchedules.map(({ close, open }, index) => {
    let newClosingTime = close;

    if (open > close) {
      const previousDay = arraySchedules?.[index + 1]
        ? arraySchedules[index + 1]
        : arraySchedules[0];
      newClosingTime = previousDay.close;
    }

    return { open, close: newClosingTime };
  }, []);
}

export function filterLocationsByDistance({
  center,
  locations,
  distance = 300000, // 300km in meters
}: {
  center: LocationCoordinates;
  locations: LocationItems;
  distance?: number;
}): LocationItems {
  return locations.filter(
    location =>
      haversineDistance(center, {
        lat: location.lat,
        lng: location.lng,
      }) <= distance,
  );
}

export function sortLocationsByDistance<
  T extends Partial<LocationItem> & { distance?: number },
>({
  center,
  locations,
}: {
  center: LocationCoordinates;
  locations: Partial<LocationItem>[];
}): T[] {
  if (!center) {
    return locations as T[];
  }

  return locations
    .map(location => {
      location['distance'] = haversineDistance(center, {
        lat: location.lat,
        lng: location.lng,
      });

      return location as T;
    })
    .sort((a, b) => b.distance - a.distance);
}
